/**
 * Which plan is real — pure, no Firebase.
 *
 * Three imports each assert something about a student's plan and none of them
 * can see the others. The freshest dated plan wins. A tag with no date never
 * wins. Disagreement is reported, never applied.
 *
 * See Documentation/specs/IMPORT_PLAN_EVIDENCE.md.
 */

import { PLAN_SIGNAL_MIN_DATE_COVERAGE } from './seisPlanType.js'

export { PLAN_SIGNAL_MIN_DATE_COVERAGE }

/** Default: a date more than one school year past due is stale. */
export const DEFAULT_STALE_AFTER_YEARS = 1

export const SEIS_STATUS_PENDING = 'Pending'
export const SEIS_STATUS_DETERMINED = 'Eligible/Previously Eligible'

const VERDICT_META = {
  active504: {
    blocked: false,
    write504: true,
    report: ''
  },
  active504Overdue: {
    blocked: false,
    write504: true,
    report: '504 review overdue'
  },
  supersededByIep: {
    blocked: false,
    write504: false,
    report: '504 not exited in Aeries'
  },
  pendingIep: {
    blocked: false,
    write504: true,
    report: 'IEP evaluation in progress'
  },
  iepExited: {
    blocked: false,
    write504: true,
    report: 'Exited SEIS — confirm the 504 is the live plan'
  },
  staleOnly504: {
    blocked: true,
    write504: false,
    report: 'Plan may have lapsed — confirm'
  },
  conflict: {
    blocked: true,
    write504: false,
    report: 'Needs a human'
  },
  tagOnly: {
    blocked: false,
    write504: false,
    report: ''
  },
  unrecognized: {
    blocked: true,
    write504: false,
    report: 'Unrecognized Student Eligibility Status — not written'
  },
  none: {
    blocked: false,
    write504: false,
    report: ''
  }
}

function cell(value) {
  return String(value ?? '').trim()
}

function pick(row, keys) {
  if (!row || typeof row !== 'object') return ''
  for (const key of keys) {
    if (row[key] != null && cell(row[key]) !== '') return row[key]
  }
  return ''
}

/**
 * Parse YYYY-MM-DD or MM/DD/YYYY to a UTC date at noon (avoids TZ flips).
 * @returns {Date|null}
 */
export function parsePlanDate(value) {
  const raw = cell(value)
  if (!raw) return null
  let iso = ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) iso = raw
  else {
    const mdy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (mdy) {
      iso = `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`
    }
  }
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  return Number.isNaN(date.getTime()) ? null : date
}

export function toIsoPlanDate(value) {
  const date = value instanceof Date ? value : parsePlanDate(value)
  if (!date) return ''
  return date.toISOString().slice(0, 10)
}

/**
 * School-year start calendar year. July–June. "2026-2027" → 2026.
 * A date in March 2026 belongs to 2025-2026.
 */
export function schoolYearStartYear(input, fallbackDate = new Date()) {
  if (typeof input === 'string') {
    const match = input.trim().match(/^(\d{4})\s*[-/–]\s*(\d{4})$/)
    if (match) return Number(match[1])
    const yearOnly = input.trim().match(/^(\d{4})$/)
    if (yearOnly) return Number(yearOnly[1])
  }
  const date = input instanceof Date ? input : fallbackDate
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()
  return month >= 6 ? year : year - 1
}

function utcToday(now) {
  const d = now instanceof Date ? now : new Date(now || Date.now())
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0))
}

/**
 * Classify a plan date against the current school year.
 * Upcoming or same-year-overdue → fresh. More than staleAfterYears behind → stale.
 */
export function classifyPlanDate(
  value,
  { now, schoolYear, staleAfterYears = DEFAULT_STALE_AFTER_YEARS } = {}
) {
  const iso = toIsoPlanDate(value)
  const date = parsePlanDate(iso)
  if (!date) {
    return {
      iso: '',
      hasDate: false,
      past: false,
      overdue: false,
      stale: false,
      fresh: false,
      yearsBehind: null
    }
  }
  const today = utcToday(now)
  const currentStart = schoolYearStartYear(schoolYear, today)
  const dateStart = schoolYearStartYear(date)
  const yearsBehind = currentStart - dateStart
  const past = date.getTime() < today.getTime()
  const stale = past && yearsBehind > staleAfterYears
  const overdue = past && !stale
  return {
    iso,
    hasDate: true,
    past,
    overdue,
    stale,
    fresh: !stale,
    yearsBehind
  }
}

export function normalizeSeisEligibilityStatus(value) {
  const raw = cell(value)
  if (!raw) return { raw: '', pending: false, determined: false, unrecognized: false }
  const compact = raw.toLowerCase().replace(/\s+/g, ' ')
  if (compact === 'pending') {
    return { raw, pending: true, determined: false, unrecognized: false }
  }
  if (compact === 'eligible/previously eligible' || compact === 'eligible / previously eligible') {
    return { raw, pending: false, determined: true, unrecognized: false }
  }
  return { raw, pending: false, determined: false, unrecognized: true }
}

export function isSeisExited(value) {
  return cell(value).toLowerCase() === 'yes'
}

// Exact match only. Aeries exports single program codes; loose digit
// matching would read "X-4" or "4th" as a 504 tag.
function u5Is504(value) {
  return cell(value) === '4'
}

function hasSeisPresence(seisRow, studentDoc) {
  if (seisRow && typeof seisRow === 'object') {
    const keys = Object.keys(seisRow).filter((k) => cell(seisRow[k]) !== '')
    if (keys.length) return true
  }
  const snap = studentDoc?.seis
  if (snap && typeof snap === 'object') {
    return Object.keys(snap).some((k) => k !== 'lastSeisImport' && cell(snap[k]) !== '')
  }
  return false
}

function laterDate(a, b) {
  const da = parsePlanDate(a)
  const db = parsePlanDate(b)
  if (!da) return b || ''
  if (!db) return a || ''
  return da.getTime() >= db.getTime() ? toIsoPlanDate(a) : toIsoPlanDate(b)
}

/**
 * Collect cheap per-student signals from an Aeries row, a SEIS row, and/or
 * the student document already in the app.
 *
 * @param {{
 *   ssid?: string,
 *   aeriesRow?: object,
 *   seisRow?: object,
 *   studentDoc?: object,
 *   schoolYear?: string,
 *   now?: Date,
 *   staleAfterYears?: number
 * }} input
 */
export function collectPlanEvidence({
  ssid = '',
  aeriesRow = null,
  seisRow = null,
  studentDoc = null,
  schoolYear = '',
  now = new Date(),
  staleAfterYears = DEFAULT_STALE_AFTER_YEARS
} = {}) {
  const aeries = aeriesRow || {}
  const seis = seisRow || studentDoc?.seis || {}
  const opts = { now, schoolYear, staleAfterYears }

  const u5 = pick(aeries, ['STU.U5', 'U5', 'User5', 'user5', 'u5', 'programCode'])
  const fofSd = pick(aeries, ['FOF.SD', 'SD', 'fofSd', 'effectiveDate'])
  const fofEd = pick(aeries, ['FOF.ED', 'ED', 'fofEd', 'exitDate'])
  const fofRdRaw = pick(aeries, ['FOF.RD', 'RD', 'fofRd', 'nextReviewDate', 'reviewDate'])

  const fofRd = classifyPlanDate(fofRdRaw, opts)
  const fofExists =
    cell(fofSd) !== '' || fofRd.hasDate || cell(pick(aeries, ['FOF.DE', 'DE'])) !== ''
  const fofActive = fofExists && cell(fofEd) === ''

  const statusRaw = pick(seis, [
    'Student Eligibility Status',
    'studentEligibilityStatus',
    'StudentEligibilityStatus'
  ])
  const exitedRaw = pick(seis, ['Student Exited', 'studentExited', 'StudentExited'])
  const status = normalizeSeisEligibilityStatus(statusRaw)
  const seisExited = isSeisExited(exitedRaw)
  const inSeis = hasSeisPresence(seisRow, studentDoc)

  const seisReview = classifyPlanDate(
    pick(seis, [
      'Date of Next Annual Plan Review',
      'Next Plan Review',
      'Annual_Review_Date',
      'reviewDate'
    ]),
    opts
  )
  const seisReeval = classifyPlanDate(
    pick(seis, ['Date of Next Reevaluation', 'Next Reevaluation', 'Triennial_Date', 'reevalDate']),
    opts
  )
  const seisBestIso = laterDate(seisReview.iso, seisReeval.iso)
  const seisBest = seisBestIso
    ? classifyPlanDate(seisBestIso, opts)
    : seisReview.hasDate
      ? seisReview
      : seisReeval

  const resolvedSsid =
    cell(ssid) ||
    cell(pick(aeries, ['STU.CID', 'SSID', 'ssid', 'State Student ID'])) ||
    cell(pick(seis, ['SSID', 'ssid'])) ||
    cell(studentDoc?.app?.studentData?.ssid)

  return {
    ssid: resolvedSsid,
    u5Says504: u5Is504(u5),
    u5Raw: cell(u5),
    fofExists,
    fofActive,
    fofFresh: fofActive && fofRd.fresh,
    fofOverdue: fofActive && fofRd.overdue,
    fofStale: fofExists && fofRd.stale,
    fofSd: cell(fofSd),
    fofEd: cell(fofEd),
    fofRd: fofRd.iso,
    fofRdClass: fofRd,
    inSeis,
    seisFresh: inSeis && seisBest.fresh,
    seisOverdue: inSeis && seisBest.overdue,
    seisStale: inSeis && seisBest.stale,
    seisPending: inSeis && status.pending,
    seisDetermined: inSeis && status.determined,
    seisExited,
    seisStatusUnrecognized: inSeis && status.unrecognized,
    seisStatusRaw: status.raw,
    seisExitedRaw: cell(exitedRaw),
    seisReviewDate: seisReview.iso,
    seisReevalDate: seisReeval.iso,
    seisBestDate: seisBest.iso,
    seisBestClass: seisBest,
    appPlan: cell(studentDoc?.app?.studentData?.plan),
    schoolYear: schoolYear || `${schoolYearStartYear(now)}-${schoolYearStartYear(now) + 1}`,
    staleAfterYears
  }
}

function seisNewerThanFof(signals) {
  const seis = parsePlanDate(signals.seisBestDate)
  const fof = parsePlanDate(signals.fofRd)
  if (!seis || !fof) return false
  return seis.getTime() > fof.getTime()
}

function bothFresh(signals) {
  return signals.fofFresh && signals.seisFresh && !signals.seisExited && !signals.seisPending
}

/**
 * @param {ReturnType<typeof collectPlanEvidence>} signals
 * @param {{ staleAfterYears?: number }} [options]
 * @returns {{ verdict: string, reason: string, blocked: boolean, write504: boolean, report: string, drivingDate: string }}
 */
export function resolvePlanVerdict(signals, options = {}) {
  const staleAfterYears =
    options.staleAfterYears ?? signals.staleAfterYears ?? DEFAULT_STALE_AFTER_YEARS
  const finish = (verdict, reason, drivingDate = '') => {
    const meta = VERDICT_META[verdict] || VERDICT_META.none
    return {
      verdict,
      reason,
      blocked: meta.blocked,
      write504: meta.write504,
      report: meta.report,
      drivingDate: drivingDate || signals.fofRd || signals.seisBestDate || '',
      staleAfterYears
    }
  }

  if (signals.seisStatusUnrecognized && (signals.fofExists || signals.u5Says504)) {
    return finish(
      'unrecognized',
      `Student Eligibility Status "${signals.seisStatusRaw}" is not Pending or Eligible/Previously Eligible.`,
      signals.seisBestDate
    )
  }

  if (signals.u5Says504 && !signals.fofExists && !signals.inSeis) {
    return finish(
      'tagOnly',
      'U5 says 504 and there is no FOF plan and no SEIS row. Schedule import may fill a blank plan field from the tag.'
    )
  }

  if (
    signals.u5Says504 &&
    !signals.fofExists &&
    signals.inSeis &&
    signals.seisDetermined &&
    !signals.seisExited
  ) {
    return finish(
      'supersededByIep',
      'U5 says 504 but there is no FOF plan and SEIS shows a determined IEP. Do not label 504 from the tag.',
      signals.seisBestDate
    )
  }

  if (signals.fofActive && signals.fofFresh && signals.seisPending) {
    return finish(
      'pendingIep',
      'Active 504 with a SEIS Pending eligibility status — the 504 is still the live plan. Do not write planStatus: initial.',
      signals.fofRd
    )
  }

  if (signals.fofActive && signals.inSeis && signals.seisExited) {
    return finish(
      'iepExited',
      'SEIS Student Exited is Yes and a 504 plan is still active. Confirm the 504 is the live plan.',
      signals.fofRd
    )
  }

  if (
    signals.fofExists &&
    signals.inSeis &&
    signals.seisDetermined &&
    !signals.seisExited &&
    signals.seisFresh &&
    seisNewerThanFof(signals)
  ) {
    return finish(
      'supersededByIep',
      `SEIS date ${signals.seisBestDate} is newer than FOF.RD ${signals.fofRd || '(none)'}. Do not write 504 accommodations.`,
      signals.seisBestDate
    )
  }

  if (bothFresh(signals) && signals.seisDetermined) {
    return finish(
      'conflict',
      `Both plans look current (FOF.RD ${signals.fofRd || '—'}, SEIS ${signals.seisBestDate || '—'}).`,
      signals.seisBestDate || signals.fofRd
    )
  }

  if (signals.fofExists && signals.inSeis && signals.seisDetermined && !signals.seisExited) {
    return finish(
      'conflict',
      'FOF and a determined SEIS row disagree and recency cannot decide.',
      signals.fofRd || signals.seisBestDate
    )
  }

  if (signals.fofExists && signals.fofStale && !signals.inSeis) {
    return finish(
      'staleOnly504',
      `FOF.RD ${signals.fofRd} is more than ${staleAfterYears} school year${staleAfterYears === 1 ? '' : 's'} past due and there is no SEIS row.`,
      signals.fofRd
    )
  }

  if (signals.fofActive && signals.fofOverdue && !signals.inSeis) {
    return finish(
      'active504Overdue',
      `FOF.RD ${signals.fofRd} is past due but still within ${staleAfterYears} school year${staleAfterYears === 1 ? '' : 's'}.`,
      signals.fofRd
    )
  }

  if (signals.fofActive && signals.fofFresh && !signals.inSeis) {
    return finish('active504', 'Active 504 with a current review date. Not in SEIS.', signals.fofRd)
  }

  if (signals.u5Says504 && !signals.fofExists && signals.inSeis) {
    return finish(
      'tagOnly',
      'U5 says 504, no FOF plan. SEIS is present but is not a determined live IEP — leave the schedule tag rule as today.',
      signals.seisBestDate
    )
  }

  // Catch-all: a student with an active FOF plan is IN the 504 file. They
  // must either write or be listed — never silently vanish. Two known ways
  // to land here: an active plan with no FOF.RD at all, and a stale plan
  // alongside a SEIS Pending status.
  if (signals.fofActive) {
    return finish(
      'conflict',
      signals.fofRd
        ? `Active 504 (FOF.RD ${signals.fofRd}) with SEIS evidence recency cannot decide.`
        : 'Active 504 plan with no review date — nothing to judge freshness by. Confirm before writing.',
      signals.fofRd || signals.seisBestDate
    )
  }

  return finish('none', 'No 504 write and no disagreement to report.')
}

/**
 * File-level coverage: if too few rows carry dates, recency proves nothing.
 */
export function assessPlanEvidenceCoverage(signalsList = []) {
  const total = signalsList.length
  const withDates = signalsList.filter((s) => s.fofRd || s.seisBestDate).length
  const coverage = total ? withDates / total : 0
  const usable = total > 0 && coverage >= PLAN_SIGNAL_MIN_DATE_COVERAGE
  return {
    total,
    withDates,
    coverage,
    usable,
    reason: usable
      ? ''
      : total === 0
        ? 'No students to check.'
        : `Only ${withDates} of ${total} rows carry a plan date. Recency would condemn the roster — write only where nothing is at risk (blank app field).`
  }
}

export function summarizeVerdicts(resolved = []) {
  const counts = {}
  let blocked = 0
  let write504 = 0
  for (const item of resolved) {
    const verdict = item?.verdict || 'none'
    counts[verdict] = (counts[verdict] || 0) + 1
    if (item?.blocked) blocked++
    if (item?.write504) write504++
  }
  return {
    total: resolved.length,
    counts,
    blocked,
    write504
  }
}
